<template>
    <div>
        <div class="headingWizard">Enquire Appointment Details</div>
        <div class="widthContainer">
            <div class="pageLayout">
                <form autocomplete="off" @submit.prevent="onFormSubmit">
                    <div class="formConatiner formConatinerTop">
                        <a class="headingText">
                            <h5 class="m-0">Enquire Appointment Details</h5>
                        </a>
                        <div class="formFields">
                            <div class="paddingSection">
                                <div class="p-grid p-fluid">
                                    <div class="p-col-12 p-md-7">
                                        <label>
                                            Appointment Reference ID<span class="requiredIcon">&#42;</span>
                                        </label>
                                        <InputText tabindex="1" type="number" v-focus
                                            v-model="v$.enquireDetails.referenceNumber.$model"
                                            @keypress="allowOnlyDigits"
                                            :class="{ 'p-error': v$.enquireDetails.referenceNumber.$error }" />
                                        <div class="error"
                                            v-if="v$.enquireDetails.referenceNumber.$dirty && v$.enquireDetails.referenceNumber.required.$invalid">
                                            Appointment Reference ID is required
                                        </div>
                                        <div class="error"
                                            v-if="v$.enquireDetails.referenceNumber.$dirty && v$.enquireDetails.referenceNumber.maxValue.$invalid">
                                            Appointment Reference ID is not valid
                                        </div>
                                    </div>
                                </div>
                                <div class="p-grid p-fliud">
                                    <div class="p-col-12 p-md-7">
                                        <label class="labelFormText">
                                            Contact information used for making appointment<span
                                                class="requiredIcon">&#42;</span>
                                        </label>
                                    </div>
                                </div>
                                <div class="p-grid p-fluid">
                                    <div class="p-col-12 p-md-5">
                                        <label>Mobile Number</label>
                                        <div class="p-inputgroup">
                                            <span class="p-inputgroup-addon">+65</span>
                                            <InputText tabindex="2"
                                                v-model.number="v$.enquireDetails.mobileNumber.$model" type="text"
                                                @keypress="allowOnlyDigits"
                                                :class="{ 'p-error': v$.enquireDetails.mobileNumber.$error }" />
                                        </div>
                                        <div class="error"
                                            v-if="v$.enquireDetails.mobileNumber.$dirty && v$.enquireDetails.mobileNumber.maxLength.$invalid">
                                            Mobile number must not exceed 8 digits
                                        </div>
                                        <div class="error"
                                            v-if="v$.enquireDetails.mobileNumber.$dirty && v$.enquireDetails.mobileNumber.minLength.$invalid">
                                            Mobile number must be of 8 digits
                                        </div>
                                        <div class="error"
                                            v-if="v$.enquireDetails.mobileNumber.$dirty && v$.enquireDetails.mobileNumber.minValue.$invalid">
                                            Mobile number is not valid
                                        </div>
                                        <div class="error"
                                            v-if="v$.enquireDetails.mobileNumber.$dirty && v$.enquireDetails.mobileNumber.isValid.$invalid">
                                            Mobile number should start with 8 or 9
                                        </div>
                                    </div>
                                    <div class="p-col-12 p-md-7">
                                        <label>Email Address</label>
                                        <InputText tabindex="3" v-model="v$.enquireDetails.email.$model"
                                            :class="{ 'p-error': v$.enquireDetails.email.$error }" />
                                        <div class="error text-left text-danger"
                                            v-if="v$.enquireDetails.email.$dirty && v$.enquireDetails.email.email.$invalid">
                                            Email is not valid
                                        </div>
                                        <div class="error"
                                            v-if="v$.enquireDetails.email.$dirty && v$.enquireDetails.email.maxLength.$invalid">
                                            Email must not exceed 320 characters
                                        </div>
                                    </div>
                                </div>
                                <div class="p-grid p-fluid">
                                    <div class="p-col-12">
                                        <div class="error"
                                            v-if="(v$.enquireDetails.mobileNumber.$dirty && v$.enquireDetails.mobileNumber.required.$invalid) || (v$.enquireDetails.email.$dirty && v$.enquireDetails.email.required.$invalid)">
                                            Enter either a mobile number and/or an email address used while appointment
                                            creation
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="reCaptchaContainer">
                                <div class="error" v-if="showMessage">Failed to verify ReCaptcha</div>
                            </div>
                        </div>
                        <div class="buttonContainer marginTopButton">
                            <div class="p-grid">
                                <div class="p-col-12">
                                    <div class="float-Left">
                                        <Button tabindex="5" type="button" label="< Back" @click="onBackAction"
                                            class="linkBtn customLinkCss" />
                                    </div>
                                    <div class="float-Right">
                                        <Button type="submit" tabindex="4" label="Next" class="btn p-button-raised" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, maxLength, email, minValue, minLength, maxValue, numeric } from '@vuelidate/validators'
import { useApiService } from '@/utils/useApiService'
// import { useReCaptcha } from 'vue-recaptcha-v3'

import { useRouter } from 'vue-router'
// const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()
const router = useRouter()
const emit = defineEmits(['nextClick'])
const { fetchWrapperForPost } = useApiService()
const store = useStore()

const enquireDetails = ref({
    referenceNumber: '',
    mobileNumber: '',
    email: ''
})

const reCaptchaToken = ref('')
const isValidate = ref(false)
const isDataFound = ref(true)
const showMessage = ref(false)

const isPhoneNumber = computed(() => !enquireDetails.value.email)
const isEmail = computed(() => !enquireDetails.value.mobileNumber)


const rules = computed(() => ({
    enquireDetails: {
        referenceNumber: {
            required,
            maxValue: maxValue(9223372036854775807)
        },
        mobileNumber: {
            required: requiredIf(() => !enquireDetails.value.email),
            numeric,
            minLength: minLength(8),
            maxLength: maxLength(8),
            minValue: minValue(1),
            isValid: value => !value || /^[89]/.test(value)
        },
        email: {
            required: requiredIf(() => !enquireDetails.value.mobileNumber),
            maxLength: maxLength(320),
            email
        }
    }
}))

const v$ = useVuelidate(rules, { enquireDetails })
async function executeRecaptcha(action) {
  if (!window.grecaptcha) throw new Error('grecaptcha not loaded');
    let key = localStorage.getItem('sitekey')
  return await window.grecaptcha.execute(key, { action });
}
async function onFormSubmit() {
    v$.value.$touch()
    if (v$.value.$invalid || isValidate.value) return

    const token = await executeRecaptcha('enquireDetails')
    reCaptchaToken.value = token
    showMessage.value = false

    // Ensure mobileNumber is '' if null
    if (!enquireDetails.value.mobileNumber || enquireDetails.value.mobileNumber === 'null') {
        enquireDetails.value.mobileNumber = ''
    }

    const payload = {
        ...enquireDetails.value,
        reCaptcha: { token }
    }

    const data = await fetchWrapperForPost('api/appointments/enquiries', payload, false, true, true)
    if (!data) return

    isDataFound.value = true
    const transDt = formatTransactDateTime12(data.generateDateTime)
    const appDt = apptDateFormat(data.appointmentDate)

    await store.dispatch('reset')
    await store.dispatch('setTransactionDateString', transDt)
    await store.dispatch('setAppointmentDateString', appDt)
    await store.dispatch('setAppointmentId', data.id)
    await store.dispatch('setAppointmentAcknowlegement', data)
    emit('nextClick')
}

function onBackAction() {
    store.dispatch('reset').then(() => {
        router.push({ path: '/' })
    })
}

function allowOnlyDigits(e) {
    if (!(e.charCode >= 48 && e.charCode <= 57 || e.charCode === 13)) {
        e.preventDefault()
    }
}
function injectRecaptchaScript() {
    if (document.getElementById('recaptcha-script')) return;
    let key = localStorage.getItem('sitekey')
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
}

function loadGreCaptcha() {
    return new Promise((resolve, reject) => {
        if (window.grecaptcha) {
            resolve(window.grecaptcha);
            return;
        }
        const interval = setInterval(() => {
            if (window.grecaptcha) {
                clearInterval(interval);
                resolve(window.grecaptcha);
            }
        }, 100);
        setTimeout(() => {
            clearInterval(interval);
            reject(new Error('Failed to load grecaptcha'));
        }, 10000);
    });
}
onMounted(async () => {
    const val = store.getters.getAppointmentAcknowlegement
    if (val) {
        enquireDetails.value.referenceNumber = val.referenceNumber
        enquireDetails.value.mobileNumber = val.phoneNumber
        enquireDetails.value.email = val.email
    }
    try {
        await injectRecaptchaScript();
        const badge = document.querySelector('.grecaptcha-badge');
        if (badge) badge.style.display = 'block';
    } catch (err) {
        console.error('Failed to load ReCaptcha:', err);
    }
    await loadGreCaptcha();
})


function formatTransactDateTime12(value) {
    if (value) {
        const [dateStr, timeStrWithMs] = value.split('T');
        const timeStr = timeStrWithMs.split('.')[0]; // e.g. "14:30:00"

        const [hours, minutes, seconds] = timeStr.split(':').map(Number);

        const dateObj = new Date(dateStr);
        dateObj.setHours(hours);
        dateObj.setMinutes(minutes);
        dateObj.setSeconds(seconds);

        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        const days = [
            "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
        ];

        // Format hours for 12-hour clock
        let hours12 = hours % 12 || 12;
        let ampm = hours >= 12 ? "PM" : "AM";

        const formattedTime = `${hours12}:${String(minutes).padStart(2, '0')} ${ampm}`;

        return `${dateObj.getDate()} ${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}, ${days[dateObj.getDay()]}, ${formattedTime}`;
    }
    return value;
}
function apptDateFormat(value) {
    if (value) {
        const dtTimeArray = value.split('T');
        const dateObj = new Date(dtTimeArray[0]);

        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        const days = [
            "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
        ];

        return dateObj.getDate() + " " +
            monthNames[dateObj.getMonth()] + " " +
            dateObj.getFullYear() + ", " +
            days[dateObj.getDay()];
    }
    return value;
}
onBeforeUnmount(() => {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) badge.style.display = 'none';
});
</script>


<style scoped>
.formConatinerTop {
    margin-top: 60px;
}
</style>